import { Column } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

export type CampaignObjective =
	| 'cold_acquisition'
	| 'nurture'
	| 'reengagement'
	| 'ar_collection'
	| 'announcement';

/**
 * Who a campaign reaches when contacts are added to it (plan section 1, Q1-2).
 *
 * - `person`: each person once. A person who is a contact at several customers
 *   gets one membership, on the record at their main customer.
 * - `customer`: each person once per customer they are active at, for offers
 *   that are specific to a store or account.
 */
export type CampaignAudience = 'person' | 'customer';

export const CAMPAIGN_AUDIENCES: ReadonlyArray<CampaignAudience> = [
	'person',
	'customer',
];

export type CampaignStatus =
	| 'draft'
	| 'active'
	| 'paused'
	| 'completed'
	| 'archived';

/**
 * A campaign is a Workd-level outreach initiative / offer (e.g. "Workd ID push").
 *
 * In Instantly it fans out to many provider campaigns (each essentially a
 * sequence) — see CampaignProviderCampaignEntity — with a given contact enrolled
 * in exactly one of them. Workd is the system of record for the campaign;
 * Instantly remains the sending + deliverability engine. `provider` plus the
 * external ids on the child tables form the provider-adapter seam so the sending
 * provider can be swapped without remodeling.
 *
 * The record owner is the inherited `ownerUserId`.
 */
@Entity('campaign', {}, { auditable: true })
export class CampaignEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	// App-validated (CampaignObjective). Kept as varchar so the offer taxonomy can
	// grow without an enum-altering migration.
	@Column('varchar', { name: 'objective', length: 50, nullable: true })
	public objective!: CampaignObjective | string | null;

	@Column('varchar', { name: 'status', length: 50, default: () => "'draft'" })
	public status!: CampaignStatus | string;

	@Column('varchar', {
		name: 'provider',
		length: 50,
		default: () => "'instantly'",
	})
	public provider!: string;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('datetime', { name: 'startDate', nullable: true })
	public startDate!: Date | null;

	@Column('datetime', { name: 'endDate', nullable: true })
	public endDate!: Date | null;

	// A database default keeps rows inserted by other writers (Mission Control
	// over the API, any direct insert) on the person default without knowing
	// about the column. App-validated against CAMPAIGN_AUDIENCES.
	@Column('varchar', {
		name: 'audience',
		length: 20,
		default: () => "'person'",
	})
	public audience!: CampaignAudience | string;

	@Column('json', { name: 'settings', nullable: true })
	public settings!: Record<string, unknown> | null;
}
